import { useEffect, useState } from 'react'
import {
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CloudServerOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ApiOutlined,
  SwapOutlined,
  AppstoreOutlined,
  ProfileOutlined,
  DownloadOutlined,
  PercentageOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

// i18n: cycle labels live in the shared `billingCycle.*` namespace (de+en).
const CYCLE_VALUES = [
  'per_minute', 'hourly', 'daily', 'weekly', 'monthly',
  'quarterly', 'yearly', 'biennial', 'triennial', 'one_time',
] as const

const cycleOptions = (t: TFunction) =>
  CYCLE_VALUES.map((v) => ({ value: v, label: t(`billingCycle.${v}`, v) }))

interface Subscription {
  id: number
  customer_id: number
  product_id: number
  status: 'pending' | 'active' | 'suspended' | 'cancelled' | 'terminated'
  billing_cycle: string
  currency: string
  unit_price: string
  quantity: number
  next_due_date?: string
  auto_renew?: boolean
  discount_pct?: string
  created_at: string
  customer_name?: string
  customer_no?: string
  product_name?: string
  license_key?: string | null
}

interface ServiceInstance {
  subscription_id: number
  state: string
  external_ref?: string
  last_action_at?: string
  meta?: Record<string, unknown>
}

const statusColors: Record<string, string> = {
  pending: 'default',
  active: 'success',
  suspended: 'orange',
  paused: 'blue',
  cancelled: 'default',
  terminated: 'red',
}

const STATUS_VALUES = ['pending', 'active', 'suspended', 'paused', 'cancelled', 'terminated'] as const

// statusLabel resolves a subscription status to its localised label
// (subscriptionStatus.* namespace, de+en). Fallback = raw value.
const statusLabel = (t: TFunction, s: string) => t(`subscriptionStatus.${s}`, s)

const cycleLabels: Record<string, string> = {
  monthly: 'Monatlich',
  quarterly: 'Vierteljährlich',
  yearly: 'Jährlich',
  biennial: '2-jährlich',
  triennial: '3-jährlich',
  quadrennial: '4-jährlich',
  quinquennial: '5-jährlich',
  sexennial: '6-jährlich',
  one_time: 'Einmalig',
}

export default function Subscriptions() {
  const { t } = useTranslation()
  const { message, modal } = AntdApp.useApp()
  const qc = useQueryClient()
  const [openDetail, setOpenDetail] = useState<Subscription | null>(null)
  const navigate = useNavigate()
  void openDetail; void setOpenDetail // drawer kept for Phase-91 backwards-compat
  const [openCreate, setOpenCreate] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [productFilter, setProductFilter] = useState<number | undefined>()
  const [searchQ, setSearchQ] = useState<string>('')
  const [selectedIDs, setSelectedIDs] = useState<number[]>([])
  const [autoRenewFilter, setAutoRenewFilter] = useState<string>('')
  const [params, setParams] = useSearchParams()
  const presetCustomerID = params.get('customer_id')
    ? Number(params.get('customer_id'))
    : undefined

  // Quick-Create-Action from CustomerDetail: ?customer_id=X&action=create
  // → open the create-modal with customer pre-filled. URL gets cleaned
  // after consumption so refresh doesn't re-trigger.
  useEffect(() => {
    if (params.get('action') === 'create' && presetCustomerID) {
      setOpenCreate(true)
      const next = new URLSearchParams(params)
      next.delete('action')
      setParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const productsQ = useQuery({
    queryKey: ['products-list-sub-filter'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: { id: number; name: string }[] } }>(
        '/api/v1/products?limit=200')
      return res.data.data.items || []
    },
    staleTime: 60_000,
  })

  const listQ = useQuery({
    queryKey: ['subscriptions', statusFilter, presetCustomerID, productFilter, searchQ, autoRenewFilter],
    queryFn: async () => {
      const qp: Record<string, string> = {}
      if (statusFilter) qp.status = statusFilter
      if (presetCustomerID) qp.customer_id = String(presetCustomerID)
      if (productFilter) qp.product_id = String(productFilter)
      if (searchQ.trim()) qp.q = searchQ.trim()
      if (autoRenewFilter) qp.auto_renew = autoRenewFilter
      const res = await apiClient.get<{ data: { items: Subscription[] } }>(
        '/api/v1/subscriptions',
        { params: qp },
      )
      return res.data.data.items
    },
  })

  interface BulkResult { id: number; ok: boolean; error?: string }
  type BulkData = { results: BulkResult[]; success_count: number; total: number }

  const onBulkSuccess = (d: BulkData, successMsg: string) => {
    message.success(`${d.success_count}/${d.total} ${successMsg}`)
    const errs = d.results.filter(r => !r.ok)
    if (errs.length > 0) {
      modal.warning({
        title: t('common.partialSuccess', 'Teilweise erfolgreich'),
        content: <div>{errs.map(r => <div key={r.id}>#{r.id}: {r.error}</div>)}</div>,
      })
    }
    setSelectedIDs([])
    qc.invalidateQueries({ queryKey: ['subscriptions'] })
  }
  const onBulkError = (e: { response?: { data?: { error?: string } } }) =>
    message.error(e.response?.data?.error || 'Bulk-Aktion fehlgeschlagen')

  const bulkSuspend = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: BulkData }>('/api/v1/subscriptions/bulk/suspend', { ids: selectedIDs })
      return res.data.data
    },
    onSuccess: (d) => onBulkSuccess(d, 'Verträge gesperrt'),
    onError: onBulkError,
  })
  const bulkUnsuspend = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: BulkData }>('/api/v1/subscriptions/bulk/unsuspend', { ids: selectedIDs })
      return res.data.data
    },
    onSuccess: (d) => onBulkSuccess(d, 'Verträge reaktiviert'),
    onError: onBulkError,
  })
  const bulkCancel = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: BulkData }>('/api/v1/subscriptions/bulk/cancel', { ids: selectedIDs })
      return res.data.data
    },
    onSuccess: (d) => onBulkSuccess(d, 'Verträge gekündigt'),
    onError: onBulkError,
  })
  const bulkTerminate = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: BulkData }>('/api/v1/subscriptions/bulk/terminate', { ids: selectedIDs })
      return res.data.data
    },
    onSuccess: (d) => onBulkSuccess(d, 'Verträge beendet'),
    onError: onBulkError,
  })
  const [adjustPriceOpen, setAdjustPriceOpen] = useState(false)
  const [adjustPriceForm] = Form.useForm<{ adjustment_pct: number }>()
  interface AdjustResult { id: number; ok: boolean; error?: string; old_price?: string; new_price?: string }
  const bulkAdjustPrice = useMutation({
    mutationFn: async (pct: number) => {
      const res = await apiClient.post<{ data: { results: AdjustResult[]; success_count: number; total: number } }>(
        '/api/v1/subscriptions/bulk/adjust-price', { ids: selectedIDs, adjustment_pct: pct },
      )
      return res.data.data
    },
    onSuccess: (d) => {
      if (d.success_count === d.total) {
        message.success(`${d.success_count} ${t('subscription.pricesAdjusted', 'Preise angepasst')}`)
      } else {
        modal.warning({
          title: t('common.partialSuccess', 'Teilweise erfolgreich'),
          content: `${d.success_count}/${d.total} ${t('subscription.pricesAdjusted', 'Preise angepasst')}.`,
        })
      }
      setAdjustPriceOpen(false)
      adjustPriceForm.resetFields()
      setSelectedIDs([])
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
    },
    onError: onBulkError,
  })

  // Legacy `?open_id=N` (Phase 91) now redirects to the dedicated
  // detail page introduced in Phase 92, so CustomerDetail-deep-links
  // and direct-row-clicks both end up in the same place.
  useEffect(() => {
    const openIdRaw = params.get('open_id')
    if (!openIdRaw) return
    navigate(`/subscriptions/${openIdRaw}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fmtDate = (v?: string) =>
    v ? new Date(v).toLocaleDateString('de-DE') : '—'

  const subStatusTag = (status: string) => (
    <Tag color={statusColors[status] || 'default'}>{statusLabel(t, status)}</Tag>
  )

  const columns: ColumnsType<Subscription> = [
    {
      title: t('subscription.customer', 'Kunde'),
      dataIndex: 'customer_id',
      key: 'customer_id',
      render: (v: number, r) => (
        <Link to={`/customers/${v}`} className="font-medium">
          {r.customer_name || r.customer_no || `#${v}`}
        </Link>
      ),
    },
    {
      title: t('subscription.product', 'Produkt'),
      dataIndex: 'product_id',
      key: 'product_id',
      render: (v: number, r) => (
        <Link to={`/subscriptions/${r.id}`}>{r.product_name || `#${v}`}</Link>
      ),
    },
    {
      title: t('subscription.licenseKey', 'Lizenz-Key'),
      dataIndex: 'license_key',
      key: 'license_key',
      width: 200,
      render: (v?: string | null) => v
        ? <Typography.Text code copyable={{ text: v }} className="text-xs">{v}</Typography.Text>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: t('subscription.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => subStatusTag(v),
    },
    {
      title: t('subscription.cycle', 'Zyklus'),
      dataIndex: 'billing_cycle',
      key: 'cycle',
      width: 120,
      render: (v: string) => (
        <Typography.Text type="secondary">{cycleLabels[v] || v}</Typography.Text>
      ),
    },
    {
      title: t('subscription.price', 'Preis'),
      key: 'price',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, r) => (
        <span>
          <Typography.Text strong>
            {parseFloat(r.unit_price).toFixed(2)} {r.currency}
          </Typography.Text>
          {r.discount_pct && parseFloat(r.discount_pct) > 0 && (
            <Tag color="green" style={{ marginLeft: 4, fontSize: 11 }}>−{parseFloat(r.discount_pct)}%</Tag>
          )}
          {r.auto_renew === false && (
            <Tag color="orange" style={{ marginLeft: 4, fontSize: 11 }}>{t('subscription.manual', 'Manuell')}</Tag>
          )}
        </span>
      ),
    },
    {
      title: t('subscription.nextDue', 'Nächste Fälligkeit'),
      dataIndex: 'next_due_date',
      key: 'next_due_date',
      width: 140,
      render: (v?: string) => (
        <Typography.Text type="secondary">{fmtDate(v)}</Typography.Text>
      ),
    },
  ]

  const renderMobileCard = (r: Subscription) => (
    <div className="mob-card">
      <div className="flex-between items-start mb-6">
        <Link to={`/subscriptions/${r.id}`} className="font-semibold text-14 flex-1 mr-8">
          {r.product_name || `#${r.product_id}`}
        </Link>
        {subStatusTag(r.status)}
      </div>
      <Link to={`/customers/${r.customer_id}`} className="text-base d-block mb-6">
        {r.customer_name || r.customer_no || `#${r.customer_id}`}
      </Link>
      <div className="flex-between">
        <Typography.Text type="secondary" className="text-sm">
          {t(`billingCycle.${r.billing_cycle}`, cycleLabels[r.billing_cycle] || r.billing_cycle)}
          {r.next_due_date ? ` · Fällig: ${fmtDate(r.next_due_date)}` : ''}
        </Typography.Text>
        <Typography.Text strong>
          {parseFloat(r.unit_price).toFixed(2)} {r.currency}
        </Typography.Text>
      </div>
    </div>
  )

  return (
    <>
      <PageHeader
        icon={<CloudServerOutlined />}
        title={t('subscription.title', 'Verträge')} subtitle={t('subscription.subtitle', 'Laufende Abonnements und Verlängerungszyklen.')}
        extra={
          <Space wrap>
            <Input.Search
              placeholder={t('subscription.search', 'Kunde oder Produkt')}
              allowClear
              className="min-w-180"
              onSearch={(v) => setSearchQ(v)}
              onChange={(e) => { if (!e.target.value) setSearchQ('') }}
            />
            <Select
              allowClear
              placeholder={t('subscription.statusFilter', 'Status-Filter')}
              className="min-w-160"
              options={STATUS_VALUES.map((v) => ({ value: v, label: statusLabel(t, v) }))}
              onChange={(v) => setStatusFilter(v ?? '')}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('subscription.productFilter', 'Produkt-Filter')}
              className="min-w-180"
              options={(productsQ.data || []).map(p => ({ value: p.id, label: p.name }))}
              onChange={(v) => setProductFilter(v)}
            />
            <Select
              allowClear
              placeholder={t('subscription.autoRenewFilter', 'Abrechnung')}
              style={{ width: 160 }}
              options={[
                { value: 'true', label: t('subscription.autoRenewOn', 'Automatisch') },
                { value: 'false', label: t('subscription.autoRenewOff', 'Manuell') },
              ]}
              onChange={(v) => setAutoRenewFilter(v ?? '')}
            />
            <Button icon={<DownloadOutlined />}
              href={`/api/v1/subscriptions/export.csv${statusFilter ? '?status=' + statusFilter : ''}`}>
              {t('subscription.exportCsv', 'CSV-Export')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenCreate(true)}>
              {t('subscription.create', 'Vertrag anlegen')}
            </Button>
          </Space>
        }
      />
      {selectedIDs.length > 0 && (
        <Card className="content-card mb-12" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
          <Space wrap>
            <Typography.Text strong>{selectedIDs.length} ausgewählt</Typography.Text>
            <Popconfirm
              title={`${selectedIDs.length} Verträge sperren?`}
              onConfirm={() => bulkSuspend.mutate()}
            >
              <Button loading={bulkSuspend.isPending} icon={<PauseCircleOutlined />}>
                Sperren
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`${selectedIDs.length} Verträge reaktivieren?`}
              onConfirm={() => bulkUnsuspend.mutate()}
            >
              <Button loading={bulkUnsuspend.isPending} icon={<PlayCircleOutlined />}>
                Reaktivieren
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`${selectedIDs.length} Verträge kündigen?`}
              onConfirm={() => bulkCancel.mutate()}
            >
              <Button loading={bulkCancel.isPending} icon={<CloseCircleOutlined />}>
                Kündigen
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`${selectedIDs.length} Verträge sofort beenden? Dies kann nicht rückgängig gemacht werden.`}
              onConfirm={() => bulkTerminate.mutate()}
            >
              <Button danger loading={bulkTerminate.isPending} icon={<StopOutlined />}>
                Beenden
              </Button>
            </Popconfirm>
            <Button icon={<PercentageOutlined />} onClick={() => setAdjustPriceOpen(true)}>
              {t('subscription.priceAdjust', 'Preisanpassung')}
            </Button>
            <Button type="text" onClick={() => setSelectedIDs([])}>{t('common.clearSelection', 'Auswahl leeren')}</Button>
          </Space>
        </Card>
      )}
      <Card className="content-card">
        <DataTable<Subscription>
          rowKey="id"
          columns={columns}
          dataSource={listQ.data || []}
          loading={listQ.isLoading}
          searchPlaceholder={t('subscription.searchPlaceholder', 'Suche Kunde, Produkt oder Status')}
          renderMobileCard={renderMobileCard}
          rowSelection={{
            selectedRowKeys: selectedIDs,
            onChange: (keys) => setSelectedIDs(keys as number[]),
            preserveSelectedRowKeys: true,
          }}
        />
      </Card>
      <SubscriptionDetailDrawer sub={openDetail} onClose={() => setOpenDetail(null)} />
      <CreateSubscriptionModal open={openCreate}
        presetCustomerID={presetCustomerID}
        onClose={() => setOpenCreate(false)} />
      <Modal
        open={adjustPriceOpen}
        title={t('subscription.priceAdjustTitle', { count: selectedIDs.length, defaultValue: 'Preisanpassung für {{count}} Vertrag/Verträge' })}
        onCancel={() => { setAdjustPriceOpen(false); adjustPriceForm.resetFields() }}
        onOk={() => adjustPriceForm.submit()}
        okText={t('subscription.adjust', 'Anpassen')}
        confirmLoading={bulkAdjustPrice.isPending}
        destroyOnHidden
      >
        <Form
          form={adjustPriceForm}
          layout="vertical"
          onFinish={(vals) => bulkAdjustPrice.mutate(vals.adjustment_pct)}
        >
          <Form.Item
            name="adjustment_pct"
            label={t('subscription.adjustPct', 'Anpassung in Prozent')}
            help={t('subscription.adjustPctHelp', 'Positiver Wert = Preiserhöhung, negativer Wert = Preissenkung. Beispiel: 3 = +3%, -5 = −5%.')}
            rules={[
              { required: true, message: 'Bitte einen Prozentwert eingeben' },
              { type: 'number', min: -90, max: 500, message: 'Wert muss zwischen -90% und 500% liegen' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              addonAfter="%"
              step={0.5}
              precision={2}
              placeholder="z.B. 3.0"
            />
          </Form.Item>
          <Typography.Text type="secondary">
            Die neuen Preise werden sofort gespeichert. Laufende Abrechnungszyklen sind nicht betroffen — der neue Preis gilt ab der nächsten Abrechnung.
          </Typography.Text>
        </Form>
      </Modal>
    </>
  )
}

function SubscriptionDetailDrawer({
  sub,
  onClose,
}: {
  sub: Subscription | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const screens = Grid.useBreakpoint()
  const [showChangePlan, setShowChangePlan] = useState(false)

  const serviceQ = useQuery({
    enabled: !!sub,
    queryKey: ['subscription-service', sub?.id],
    queryFn: async () => {
      if (!sub) return null
      try {
        const res = await apiClient.get<{ data: ServiceInstance }>(
          `/api/v1/subscriptions/${sub.id}/service`,
        )
        return res.data.data
      } catch {
        return null
      }
    },
  })

  const action = useMutation({
    mutationFn: async (verb: string) => {
      if (!sub) return
      await apiClient.post(`/api/v1/subscriptions/${sub.id}/${verb}`)
    },
    onSuccess: (_d, verb) => {
      message.success(t(`subscription.actionDone.${verb}`, verb))
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
      qc.invalidateQueries({ queryKey: ['subscription-service', sub?.id] })
      onClose()
    },
  })

  const buttonFor = (verb: string, icon: React.ReactNode, label: string, danger = false) => (
    <Button
      key={verb}
      icon={icon}
      danger={danger}
      disabled={!sub || actionAvailability(sub.status, verb) === false}
      onClick={() => action.mutate(verb)}
      loading={action.isPending}
    >
      {label}
    </Button>
  )

  const mrr = sub
    ? (parseFloat(sub.unit_price) * sub.quantity).toFixed(2)
    : '0.00'
  const nextDueLabel = sub?.next_due_date
    ? new Date(sub.next_due_date).toLocaleDateString('de-DE')
    : '—'

  return (
    <Drawer
      open={!!sub}
      onClose={onClose}
      width={screens.xl ? 760 : screens.md ? 620 : '100vw'}
      title={sub ? (
        <Space wrap>
          <span>{t('subscription.title', 'Vertrag')} #{sub.id}</span>
          <Tag color={statusColors[sub.status]}>{sub.status}</Tag>
          {serviceQ.data && (
            <Tag><ApiOutlined /> {serviceQ.data.state}</Tag>
          )}
        </Space>
      ) : ''}
      destroyOnClose
    >
      {sub && (
        <Tabs
          defaultActiveKey="overview"
          items={[
            {
              key: 'overview',
              label: <span><ProfileOutlined /> {t('subscription.tabs.overview', 'Übersicht')}</span>,
              children: (
                <>
                  {/* KPI strip */}
                  <div className="kpi-grid kpi-grid-4 mb-16">
            <div className="kpi-tile">
              <span className="kpi-tile-label">
                <DollarOutlined /> {t('subscription.mrr', 'Periode-Wert')}
              </span>
              <span className="kpi-tile-value">{mrr} {sub.currency}</span>
              <span className="kpi-tile-sub">
                {sub.unit_price} {sub.currency}
                {sub.quantity > 1 ? ` × ${sub.quantity}` : ''} · {t(`billingCycle.${sub.billing_cycle}`, sub.billing_cycle)}
              </span>
            </div>
            <div className="kpi-tile">
              <span className="kpi-tile-label">
                <CalendarOutlined /> {t('subscription.nextDue', 'Nächste Fälligkeit')}
              </span>
              <span className="kpi-tile-value-sm">{nextDueLabel}</span>
              <span className="kpi-tile-sub">
                {sub.status === 'active'
                  ? t('subscription.autoRenew', 'Auto-Verlängerung')
                  : t('subscription.noRenew', 'Keine Verlängerung')}
              </span>
            </div>
            <div className={`kpi-tile ${
              sub.status === 'active' ? 'kpi-success' :
              sub.status === 'suspended' ? 'kpi-warning' :
              sub.status === 'terminated' ? 'kpi-danger' : ''
            }`}>
              <span className="kpi-tile-label">
                <CloudServerOutlined /> {t('subscription.status', 'Vertrags-Status')}
              </span>
              <span className="kpi-tile-value-sm">{sub.status}</span>
              <span className="kpi-tile-sub">
                {t('subscription.product', 'Produkt')} #{sub.product_id}
              </span>
            </div>
            <div className="kpi-tile">
              <span className="kpi-tile-label">
                <ClockCircleOutlined /> {t('subscription.age', 'Vertragsalter')}
              </span>
              <span className="kpi-tile-value-sm">
                {Math.floor((Date.now() - new Date(sub.created_at).getTime()) /
                  (1000 * 60 * 60 * 24))} {t('subscription.days', 'Tage')}
              </span>
              <span className="kpi-tile-sub">
                {t('subscription.since', 'seit')} {new Date(sub.created_at).toLocaleDateString('de-DE')}
              </span>
            </div>
          </div>

          {/* Quick-info card */}
          <Card className="content-card mb-12" size="small"
            title={t('subscription.master', 'Stammdaten')}>
            <Descriptions column={screens.md ? 2 : 1} size="small" bordered>
              <Descriptions.Item label={t('subscription.customer', 'Kunde')}>
                <Link to={`/customers/${sub.customer_id}`}>#{sub.customer_id}</Link>
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.product', 'Produkt')}>
                #{sub.product_id}
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.cycle', 'Abrechnungszyklus')}>
                {t(`billingCycle.${sub.billing_cycle}`, sub.billing_cycle)}
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.currency', 'Währung')}>
                {sub.currency}
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.quantity', 'Menge')}>
                {sub.quantity}
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.unitPrice', 'Einzelpreis')}>
                {sub.unit_price} {sub.currency}
              </Descriptions.Item>
              <Descriptions.Item label={t('subscription.created', 'Angelegt')} span={2}>
                {new Date(sub.created_at).toLocaleString('de-DE')}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Service instance card */}
          <Card className="content-card mb-12" size="small"
            title={<><ApiOutlined /> {t('subscription.service', 'Provisioning / Service-Instanz')}</>}>
            {serviceQ.data ? (
              <Descriptions column={screens.md ? 3 : 1} size="small" bordered>
                <Descriptions.Item label={t('subscription.svcState', 'Status')}>
                  <Tag color={serviceQ.data.state === 'active' ? 'success' : 'default'}>
                    {serviceQ.data.state}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('subscription.svcExtRef', 'Externe Ref.')}>
                  {serviceQ.data.external_ref
                    ? <Typography.Text code>{serviceQ.data.external_ref}</Typography.Text>
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label={t('subscription.svcLastAction', 'Letzte Aktion')}>
                  {serviceQ.data.last_action_at
                    ? new Date(serviceQ.data.last_action_at).toLocaleString('de-DE')
                    : '—'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Typography.Text type="secondary">
                {t('subscription.noService', 'Noch keine Service-Instanz angelegt.')}
              </Typography.Text>
            )}
          </Card>

                  {/* Actions */}
                  <Card className="content-card" size="small"
                    title={t('subscription.actions', 'Aktionen')}>
                    <Space wrap>
                      {buttonFor('activate', <PlayCircleOutlined />, t('subscription.activate', 'Aktivieren'))}
                      {buttonFor('suspend', <PauseCircleOutlined />, t('subscription.suspend', 'Pausieren'))}
                      {buttonFor('unsuspend', <PlayCircleOutlined />, t('subscription.unsuspend', 'Fortsetzen'))}
                      <Button icon={<SwapOutlined />}
                        disabled={sub.status !== 'active' && sub.status !== 'suspended'}
                        onClick={() => setShowChangePlan(true)}>
                        {t('subscription.changePlan', 'Plan wechseln')}
                      </Button>
                      {buttonFor('cancel', <CloseCircleOutlined />, t('subscription.cancel', 'Kündigen'))}
                      {buttonFor('terminate', <StopOutlined />, t('subscription.terminate', 'Beenden'), true)}
                    </Space>
                  </Card>
                </>
              ),
            },
            {
              key: 'customfields',
              label: <span><AppstoreOutlined /> {t('subscription.tabs.customFields', 'Zusatzfelder')}</span>,
              children: <SubscriptionCustomFieldsTab subscriptionID={sub.id} />,
            },
          ]}
        />
      )}
      <ChangePlanModal
        sub={showChangePlan ? sub : null}
        onClose={() => setShowChangePlan(false)}
        onApplied={() => {
          setShowChangePlan(false)
          qc.invalidateQueries({ queryKey: ['subscriptions'] })
          onClose()
        }}
      />
    </Drawer>
  )
}

// ── Subscription Custom-Fields Tab ────────────────────────────────────

interface SubCustomFieldDef {
  id: number
  key: string
  label: string
  kind: string
  options: Record<string, unknown>
  required: boolean
}

function SubscriptionCustomFieldsTab({ subscriptionID }: { subscriptionID: number }) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()

  const defsQ = useQuery({
    queryKey: ['custom-fields-defs', 'subscription'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: SubCustomFieldDef[] }>(
        '/api/v1/custom-fields?entity=subscription&active=true')
      return res.data.data || []
    },
  })

  const valsQ = useQuery({
    queryKey: ['custom-fields-vals', 'subscription', subscriptionID],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Record<string, string> }>(
        `/api/v1/custom-fields/subscription/${subscriptionID}/values`)
      return res.data.data || {}
    },
  })

  // Hydrate form from server vals when both queries are loaded.
  if (valsQ.data && !form.isFieldsTouched()) {
    form.setFieldsValue(valsQ.data)
  }

  const save = useMutation({
    mutationFn: async (vals: Record<string, string>) => {
      await apiClient.put(
        `/api/v1/custom-fields/subscription/${subscriptionID}/values`, vals)
    },
    onSuccess: () => {
      message.success(t('subscription.cfSaved', 'Zusatzfelder gespeichert'))
      qc.invalidateQueries({
        queryKey: ['custom-fields-vals', 'subscription', subscriptionID],
      })
    },
  })

  if (defsQ.isLoading) {
    return <Typography.Text type="secondary">{t('common.loading', 'Lädt…')}</Typography.Text>
  }

  if ((defsQ.data || []).length === 0) {
    return (
      <Typography.Paragraph type="secondary">
        {t('subscription.noCustomFields',
          'Keine Zusatzfelder für Verträge definiert. Felder können unter Einstellungen → Zusatzfelder (Entity „Verträge") angelegt werden.')}
      </Typography.Paragraph>
    )
  }

  return (
    <Form form={form} layout="vertical"
      onFinish={(v) => save.mutate(v as Record<string, string>)}>
      {(defsQ.data || []).map(d => (
        <Form.Item key={d.key} name={d.key} label={d.label}
          rules={d.required ? [{ required: true }] : []}>
          {renderSubCFInput(d)}
        </Form.Item>
      ))}
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={save.isPending}>
          {t('common.save', 'Speichern')}
        </Button>
      </Form.Item>
    </Form>
  )
}

function renderSubCFInput(d: SubCustomFieldDef) {
  switch (d.kind) {
    case 'textarea': return <Input.TextArea rows={3} />
    case 'number': return <Input type="number" />
    case 'bool': return <Switch />
    case 'date': return <Input type="date" />
    case 'select': {
      const values = (d.options as { values?: string[] })?.values || []
      return <Select options={values.map(v => ({ value: v, label: v }))} allowClear />
    }
    default: return <Input />
  }
}

function actionAvailability(status: string, verb: string): boolean {
  // Mirror backend state-machine guards. Greys out useless buttons.
  switch (verb) {
    case 'activate':   return status === 'pending'
    case 'suspend':    return status === 'active'
    case 'unsuspend':  return status === 'suspended'
    case 'cancel':     return status === 'active' || status === 'suspended'
    case 'terminate':  return status !== 'terminated'
  }
  return false
}

function CreateSubscriptionModal({
  open,
  onClose,
  presetCustomerID,
}: {
  open: boolean
  onClose: () => void
  presetCustomerID?: number
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const screens = Grid.useBreakpoint()
  const [form] = Form.useForm()

  const create = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      // Accept German decimal input (0,00) by normalizing comma → dot on the
      // monetary fields before the backend (which expects 0.00) sees them.
      const body = { ...values }
      for (const k of ['unit_price', 'setup_fee']) {
        if (typeof body[k] === 'string') body[k] = (body[k] as string).replace(',', '.').trim()
      }
      await apiClient.post('/api/v1/subscriptions', body)
    },
    onSuccess: () => {
      message.success(t('subscription.createSuccess', 'Vertrag angelegt'))
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
      form.resetFields()
      onClose()
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const customersQ = useQuery({
    enabled: open,
    queryKey: ['customers-for-sub'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: { id: number; customer_no: string; company?: string; email?: string }[] } }>(
        '/api/v1/customers?limit=200',
      )
      return res.data.data.items
    },
  })

  const productsQ = useQuery({
    enabled: open,
    queryKey: ['products-for-sub'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: { id: number; slug: string; name: string }[] } }>(
        '/api/v1/products?limit=200',
      )
      return res.data.data.items
    },
  })

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('subscription.create', 'Vertrag anlegen')}
      onOk={() => form.submit()}
      okButtonProps={{ loading: create.isPending }}
      width={screens.sm ? 560 : '95vw'}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ billing_cycle: 'monthly', currency: 'EUR',
          quantity: 1, customer_id: presetCustomerID }}
        onFinish={(v) => create.mutate(v)}
      >
        <Form.Item name="customer_id" label={t('subscription.customer', 'Kunde')}
          rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={(customersQ.data || []).map(c => ({
              value: c.id,
              label: `${c.customer_no} — ${c.company || c.email || '?'}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="product_id" label={t('subscription.product', 'Produkt')}
          rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={(productsQ.data || []).map(p => ({
              value: p.id,
              label: `${p.slug} — ${p.name}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="billing_cycle" label={t('subscription.cycle', 'Zyklus')}>
          <Select options={cycleOptions(t)} />
        </Form.Item>
        <Form.Item name="currency" label={t('subscription.currency', 'Währung')} rules={[{ required: true }]}>
          <Input maxLength={3} />
        </Form.Item>
        <Form.Item name="unit_price" label={t('subscription.unitPrice', 'Stückpreis')}
          rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="quantity" label={t('subscription.quantity', 'Menge')}>
          <Input type="number" min={1} />
        </Form.Item>
        <Form.Item name="setup_fee" label={t('subscription.setupFee', 'Einrichtungsgebühr')}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  )
}

interface ChangePlanPreview {
  old_product_id: number
  old_billing_cycle: string
  old_unit_price: string
  new_product_id: number
  new_billing_cycle: string
  new_unit_price: string
  prorata_credit: string
  currency: string
  days_remaining: number
  cycle_days: number
}

function ChangePlanModal({ sub, onClose, onApplied }: {
  sub: Subscription | null
  onClose: () => void
  onApplied: () => void
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm()
  const [preview, setPreview] = useState<ChangePlanPreview | null>(null)

  const productsQ = useQuery({
    enabled: !!sub,
    queryKey: ['products-for-plan-change'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: { id: number; slug: string; name: string }[] } }>(
        '/api/v1/products?limit=200&active=true')
      return res.data.data.items
    },
  })

  const previewMut = useMutation({
    mutationFn: async (vals: { new_product_id: number; new_billing_cycle: string }) => {
      if (!sub) return null
      const res = await apiClient.post<{ data: ChangePlanPreview }>(
        `/api/v1/subscriptions/${sub.id}/change-plan/preview`, vals)
      return res.data.data
    },
    onSuccess: (d) => setPreview(d),
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error ||
        t('subscription.previewFailed', 'Vorschau fehlgeschlagen')),
  })

  const applyMut = useMutation({
    mutationFn: async (vals: { new_product_id: number; new_billing_cycle: string; note?: string }) => {
      if (!sub) return null
      const res = await apiClient.post<{ data: ChangePlanPreview }>(
        `/api/v1/subscriptions/${sub.id}/change-plan`, vals)
      return res.data.data
    },
    onSuccess: () => {
      message.success(t('subscription.changePlanDone', 'Plan gewechselt'))
      setPreview(null)
      form.resetFields()
      onApplied()
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error ||
        t('subscription.changePlanFailed', 'Plan-Wechsel fehlgeschlagen')),
  })

  return (
    <Modal
      open={!!sub}
      onCancel={() => { setPreview(null); onClose() }}
      title={t('subscription.changePlanTitle', 'Plan wechseln')}
      width={560}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => { setPreview(null); onClose() }}>
          {t('common.cancel', 'Abbrechen')}
        </Button>,
        preview ? (
          <Button key="apply" type="primary" danger loading={applyMut.isPending}
            onClick={() => {
              const v = form.getFieldsValue()
              applyMut.mutate({
                new_product_id: v.new_product_id,
                new_billing_cycle: v.new_billing_cycle,
                note: v.note,
              })
            }}>
            {t('subscription.applyChange', 'Wechsel durchführen')}
          </Button>
        ) : (
          <Button key="preview" type="primary" loading={previewMut.isPending}
            onClick={() => form.submit()}>
            {t('subscription.preview', 'Vorschau berechnen')}
          </Button>
        ),
      ]}
    >
      <Form form={form} layout="vertical"
        initialValues={{ new_billing_cycle: sub?.billing_cycle || 'monthly' }}
        onFinish={(v) => previewMut.mutate({
          new_product_id: v.new_product_id, new_billing_cycle: v.new_billing_cycle,
        })}>
        <Form.Item name="new_product_id" label={t('subscription.newProduct', 'Neues Produkt')}
          rules={[{ required: true }]}>
          <Select
            showSearch
            disabled={!!preview}
            optionFilterProp="label"
            options={(productsQ.data || []).map(p => ({
              value: p.id,
              label: `${p.slug} — ${p.name}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="new_billing_cycle" label={t('subscription.newCycle', 'Neuer Zyklus')}>
          <Select disabled={!!preview} options={cycleOptions(t)} />
        </Form.Item>
        <Form.Item name="note" label={t('subscription.note', 'Notiz (optional)')}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>

      {preview && (
        <Card className="content-card" size="small"
          title={t('subscription.previewTitle', 'Vorschau Plan-Wechsel')}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('subscription.oldPlan', 'Alter Plan')}>
              {t('subscription.product', 'Produkt')} #{preview.old_product_id} —{' '}
              {preview.old_unit_price} {preview.currency} / {preview.old_billing_cycle}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscription.newPlan', 'Neuer Plan')}>
              {t('subscription.product', 'Produkt')} #{preview.new_product_id} —{' '}
              {preview.new_unit_price} {preview.currency} / {preview.new_billing_cycle}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscription.daysRemaining', 'Restlaufzeit')}>
              {preview.days_remaining} / {preview.cycle_days} {t('subscription.days', 'Tage')}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscription.prorataCredit', 'Pro-rata-Guthaben')}>
              <Typography.Text strong>
                {preview.prorata_credit} {preview.currency}
              </Typography.Text>
              <Typography.Text type="secondary" className="ml-8">
                {t('subscription.toWallet', '→ Kunden-Wallet')}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>
          <Typography.Paragraph type="secondary" className="mt-8 text-sm">
            {t('subscription.changePlanHint',
              'Der Restwert der aktuellen Periode wird dem Kunden-Wallet gutgeschrieben. Die nächste Abrechnung läuft automatisch zum neuen Preis (Scheduler @08:00).')}
          </Typography.Paragraph>
        </Card>
      )}
    </Modal>
  )
}
